import { makeStyles } from '@material-ui/styles'
import ToggleSwitchField from 'pf9-ui-components/built/components/validatedForm/ToggleSwitchField'
import ModalForm from 'pf9-ui-components/built/elements/modal/ModalForm'
import { prop } from 'ramda'
import { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import type { RootState } from 'app/store'
import CopyToClipboard from 'core/components/CopyToClipboard'
import Text from 'core/elements/Text'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { type SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import type Theme from 'core/themes/model'
import EtcdBackupFields from 'k8s/components/cluster/deployment/form-components/EtcdBackupFields'
import { createKaapiClusterAddonBody } from 'k8s/components/kaapi/cluster-addons/action-helpers'
import {
  createKaapiClusterAddon,
  deleteKaapiClusterAddon,
  updateKaapiClusterAddon,
} from 'k8s/components/kaapi/cluster-addons/actions'
import {
  areEtcdBackupSchedulesDeployed,
  buildEtcdBackupClusterSpec,
  type ClusterAddonStatus,
  ETCD_BACKUP_ADDON_TYPE,
  ETCD_BACKUP_ADDON_VERSION,
  type EtcdBackupFormParams,
  etcdBackupFormParamsFromSpec,
  formatBackupRun,
  getEtcdBackupSettingsError,
  getNextEtcdBackup,
} from 'k8s/components/kaapi/cluster-addons/etcd-backup'
import { AddonStatusBadge } from './AddonStatusBadge'

export interface EtcdBackupAddon {
  metadata?: { name?: string; namespace?: string }
  spec?: { clusterSpec?: { etcdBackup?: Parameters<typeof etcdBackupFormParamsFromSpec>[0] } }
  status?: ClusterAddonStatus
}

interface ConfigureEtcdBackupModalProps {
  clusterName: string
  // The cluster's Backup add-on, or undefined when it doesn't have one yet
  addon?: EtcdBackupAddon
  onClose: () => void
  onSaved: () => void
}

export default function ConfigureEtcdBackupModal({
  clusterName,
  addon,
  onClose,
  onSaved,
}: ConfigureEtcdBackupModalProps) {
  const classes = useStyles()
  const { activeKaapiTenant } = useSelector<RootState, SessionState>(prop(sessionStoreKey))
  const namespace = addon?.metadata?.namespace || activeKaapiTenant
  // CEB-FR-002: read once on open, so a refresh of the add-on list doesn't reset the form
  const initialParams = useMemo(
    () => etcdBackupFormParamsFromSpec(addon?.spec?.clusterSpec?.etcdBackup),
    [],
  )
  const { params, getParamsUpdater } = useParams<EtcdBackupFormParams>(initialParams)
  const [settingsError, setSettingsError] = useState<{ title: string; message: string } | null>(
    null,
  )

  const {
    update: createAddon,
    updating: creating,
    error: createError,
  } = useUpdateAction(createKaapiClusterAddon)
  const {
    update: patchAddon,
    updating: patching,
    error: patchError,
  } = useUpdateAction(updateKaapiClusterAddon)
  const {
    update: deleteAddon,
    updating: deleting,
    error: deleteError,
  } = useUpdateAction(deleteKaapiClusterAddon)

  const isRemoving = !!addon && !params.etcdBackup
  const isEnabling = !addon && !!params.etcdBackup

  const handleSubmit = useCallback(async () => {
    setSettingsError(null)
    // CEB-FR-005: turning Backup off removes the add-on; the files on the nodes stay
    if (!params.etcdBackup) {
      if (!addon) {
        onClose()
        return
      }
      const { success } = await deleteAddon({ namespace, name: addon.metadata?.name })
      if (success) {
        onSaved()
        onClose()
      }
      return
    }
    // CEB-FR-006: the backend needs at least one backup kind
    const kindError = getEtcdBackupSettingsError(params)
    if (kindError) {
      setSettingsError({ title: 'Invalid Backup settings', message: kindError })
      return
    }
    const clusterSpec = buildEtcdBackupClusterSpec(params)
    // CEB-FR-003: change the existing add-on; CEB-FR-004: or create it
    const { success } = addon
      ? await patchAddon({
          namespace,
          name: addon.metadata?.name,
          customBody: { spec: { clusterSpec } },
        })
      : await createAddon({
          namespace,
          body: createKaapiClusterAddonBody({
            clusterName,
            addonType: ETCD_BACKUP_ADDON_TYPE,
            version: ETCD_BACKUP_ADDON_VERSION,
            clusterSpec,
          }),
        })
    // CEB-FR-007: close on success; on failure the hook's error shows in the drawer
    if (success) {
      onSaved()
      onClose()
    }
  }, [
    params,
    addon,
    namespace,
    clusterName,
    createAddon,
    patchAddon,
    deleteAddon,
    onSaved,
    onClose,
  ])

  const status = addon?.status
  const last = status?.lastBackup
  // CEB-FR-001: times are in the add-on's time zone
  const timeZone = addon?.spec?.clusterSpec?.etcdBackup?.timeZone
  const lastBackup = last && formatBackupRun(last, timeZone)
  // CEB-FR-001: the next run of the saved schedules, not the ones being edited
  const nextRun =
    areEtcdBackupSchedulesDeployed(status) &&
    getNextEtcdBackup(addon?.spec?.clusterSpec?.etcdBackup?.schedules, timeZone)
  const nextBackup = nextRun && formatBackupRun(nextRun, timeZone)

  return (
    <ModalForm
      open
      title="Configure Backup"
      onClose={onClose}
      onSubmit={handleSubmit}
      submitTitle={isRemoving ? 'Disable Backup' : isEnabling ? 'Enable Backup' : 'Save'}
      submitting={creating || patching || deleting}
      error={settingsError || createError || patchError || deleteError}
    >
      <div className={classes.content} data-testid="configure-etcd-backup">
        {/* CEB-FR-001: the add-on's status and its last backup; a failed badge opens the
            error details */}
        {status && (
          <div className={classes.status} data-testid="etcd-backup-status">
            <div className={classes.statusHeader}>
              <Text variant="caption1">Current status</Text>
              <AddonStatusBadge addonName="Backup" status={status} />
            </div>
            {last && lastBackup ? (
              <>
                <Text variant="body2" data-testid="etcd-backup-last-backup">
                  {`Last backup ${lastBackup.relative}, at ${lastBackup.exact}, by the ${lastBackup.schedule.toLowerCase()} schedule${last.node ? ` on ${last.node}` : ''}`}
                </Text>
                {last.path && (
                  <CopyToClipboard copyText={last.path} inline codeBlock={false} triggerWithChild>
                    <Text variant="body2" className={classes.path} data-testid="etcd-backup-path">
                      {last.path}
                    </Text>
                  </CopyToClipboard>
                )}
              </>
            ) : (
              <Text variant="body2" className={classes.note} data-testid="etcd-backup-last-backup">
                No backup has finished yet
              </Text>
            )}
            {nextBackup && (
              <Text variant="body2" data-testid="etcd-backup-next-backup">
                {`Next backup ${nextBackup.relative}, at ${nextBackup.exact}, by the ${nextBackup.schedule.toLowerCase()} schedule`}
              </Text>
            )}
          </div>
        )}
        <ToggleSwitchField
          id="etcdBackup"
          label="Enable Backup"
          value={params.etcdBackup}
          onChange={getParamsUpdater('etcdBackup')}
        />
        {params.etcdBackup ? (
          <EtcdBackupFields
            params={params as Record<string, unknown>}
            getParamsUpdater={getParamsUpdater as (key: string) => (value: unknown) => void}
          />
        ) : addon ? (
          <Text variant="body2" className={classes.note} data-testid="etcd-backup-remove-note">
            Saving removes the Backup add-on and its backup schedules from the cluster. Backup files
            already on the control plane nodes are kept.
          </Text>
        ) : (
          // CEB-FR-002: Backup starts off for a cluster without it
          <Text variant="body2" className={classes.note} data-testid="etcd-backup-off-note">
            Backup isn't enabled for this cluster. Turn it on to set up backup schedules.
          </Text>
        )}
      </div>
    </ModalForm>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  content: {
    display: 'grid',
    gridGap: theme.spacing(3),
  },
  status: {
    display: 'grid',
    gridGap: theme.spacing(1),
    justifyItems: 'start',
    padding: theme.spacing(2),
    border: `1px solid ${theme.components.card.border}`,
    borderRadius: 4,
    wordBreak: 'break-word',
  },
  statusHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  path: {
    fontFamily: 'monospace',
    wordBreak: 'break-all',
  },
  note: {
    color: theme.components.typography.passive,
  },
}))
